import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { IInitialState } from "./store/reducers/models";
import { message } from "antd";
import { setError, setFollowUpData } from "./store/reducers";
import Auth from "./containers/Auth";
import Layout from "./containers/Layout";
import Departments from "./containers/Departments";
import Admin from "./containers/Admin";
import Team from "./containers/Team";
import Reports from "./containers/Reports";
import Search from "./containers/Reports/search";
import Sales from "./containers/Sales";
import Leads from "./containers/Leads";
import Dashboard from "./containers/Dashboard";
import DepartmentForm from "./containers/Departments/Form";
import AdminForm from "./containers/Admin/Form";
import TeamForm from "./containers/Team/Form";
import Lead from "./containers/Leads/Lead";
import ProtectedRoutes from "./components/ProtectedRoutes";
import SalesData from "./containers/Sales/Form";
import Referals from "./containers/Referals";
import SalesDataReports from "./containers/Sales/Sales-Report";
import Configure from "./containers/Configure";
import Notifications from "./containers/Notifications";
import TaxConfiguration from "./containers/TaxConfiguration";
import UnAuthorized from "./components/UnAuthorized";
import NotFound from "./components/NotFound";
import TeamLead from "./containers/TeamLead";
import TeamLeadForm from "./containers/TeamLead/Form";
import Support from "./containers/Support";
import Escalation from "./containers/Escalation";
import Comments from "./containers/Support/Form/Comments";
import CommentsUser from "./containers/Escalation/Form/Comments";
import GlobalFollowUpReminder from "./containers/Sales/Sales-Report/GlobalFollowUpReminder";
import moment from "moment-timezone";

import "./styles.scss";
import SalesService from "./services/Sales.services";

const routerComponents = [
  { path: "/dashboard", Component: Dashboard },
  { path: "/departments", Component: Departments },
  { path: "/departments/create", Component: DepartmentForm },
  { path: "/departments/edit/:id", Component: DepartmentForm },
  { path: "/admin", Component: Admin },
  { path: "/admin/create", Component: AdminForm },
  { path: "/admin/edit/:id", Component: AdminForm },
  { path: "/teamlead", Component: TeamLead },
  { path: "/teamlead/create", Component: TeamLeadForm },
  { path: "/teamlead/edit/:id", Component: TeamLeadForm },
  { path: "/team", Component: Team },
  { path: "/team/create", Component: TeamForm },
  { path: "/team/edit/:id", Component: TeamForm },
  { path: "/reports", Component: Reports },
  { path: "/search", Component: Search },
  { path: "/configuration", Component: Configure },
  { path: "/sales", Component: Sales },
  { path: "/sales/data-upload", Component: SalesData },
  { path: "/sales/report", Component: SalesDataReports },
  { path: "/sales/followup-calls", Component: SalesDataReports },
  { path: "/leads", Component: () => <Leads isLeads /> },
  { path: "/walkouts", Component: () => <Leads isLeads={false} /> },
  { path: "/leads/create", Component: Lead },
  { path: "/leads/edit/:id", Component: Lead },
  { path: "/referals", Component: Referals },
  { path: "/notifications", Component: Notifications },
  {
    path: "/tax-configuration",
    Component: () => (
      <TaxConfiguration
        section={"TAX_CONFIG"}
        leadData={null}
        showHeader={true}
      />
    ),
  },
  { path: "/support", Component: Support },
  { path: "/support/view/:id", Component: Comments },
  { path: "/escalation", Component: Escalation },
  { path: "/escalation/view/:id", Component: CommentsUser },
];

function App() {
  const [messageApi, contextHolder] = message.useMessage();
  const gloablStore = useSelector((state: any) => state.store);
  const { showAlert, alertMessage, alertType }: IInitialState = gloablStore;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails = localStorage.getItem("user_details") || {};

  // ✅ state to control GlobalFollowUpReminder
  const [showReminder, setShowReminder] = useState(false);
  const [followups, setFollowups] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [reminders, setReminders] = useState(0);

  const fetchFollowups = async () => {
    try {
      const response = await SalesService.getsalesList(
        `/sales/follow-ups?notification=true`
      );
      const { totalRows, data } = response || {};
      setReminders(totalRows);

      if (totalRows > 0) {
        // Show reminder immediately
        setShowReminder(true);

        // Show popup again every 15 minutes
        const intervalId = setInterval(() => {
          setShowReminder(true);
        }, 1 * 60 * 1000);

        // Store interval so we can clear it later
        return () => clearInterval(intervalId);
      } else {
        setShowReminder(false);
      }

      // Helper: Convert any Date into MST Date object
      const toMSTDate = (date: Date) =>
        new Date(
          date.toLocaleString("en-US", {
            timeZone: "America/Denver",
          })
        );

      // Current time in MST
      const nowMSTDate = toMSTDate(new Date());

      const hasFollowUpDate = data.some((item: any) => {
        if (!item.follow_up_date || !item.curdatetime) return false;

        // Use the dates as UTC without converting
        const currentDate = new Date(item.curdatetime); // current UTC time
        const followUpDate = new Date(item.follow_up_date); // follow-up UTC time

        const diff = followUpDate.getTime() - currentDate.getTime(); // difference in ms

        // Check if follow-up is within next 30 minutes
        return diff >= 0 && diff <= 30 * 60 * 1000;
      });
      dispatch(setFollowUpData(hasFollowUpDate));
    } catch (error) {
      console.error("Error fetching follow-ups:", error);
    }
  };

  useEffect(() => {
    if (Object.keys(userDetails).length > 0) {
      // Call immediately on mount
      fetchFollowups();

      // Set interval to call every 15 minutes (15 * 60 * 1000 ms)
      const intervalId = setInterval(() => {
        fetchFollowups();
      }, 15 * 60 * 1000);

      // Cleanup interval on unmount
      return () => clearInterval(intervalId);
    }
  }, [userDetails]);

  useEffect(() => {
    // Function to check rows and show reminder
    const checkReminder = () => {
      if (reminders > 0) {
        setShowReminder(true);
      } else {
        setShowReminder(false);
      }
    };

    // Run immediately on mount
    checkReminder();
    // Intervals for 1, 5, and 10 minutes
    const interval1 = setInterval(checkReminder, 5 * 60 * 1000); // every 1 min
    const interval5 = setInterval(checkReminder, 10 * 60 * 1000); // every 5 mins
    const interval10 = setInterval(checkReminder, 15 * 60 * 1000); // every 10 mins

    // Cleanup when component unmounts
    return () => {
      clearInterval(interval1);
      clearInterval(interval5);
      clearInterval(interval10);
    };
  }, [reminders]);

  useEffect(() => {
    if (showAlert) {
      messageApi.open({
        type: alertType,
        content: alertMessage,
      });

      setTimeout(() => {
        dispatch(setError({ status: false, type: undefined, message: "" }));
      }, 1000);
    }
  }, [showAlert, alertType, alertMessage, dispatch, messageApi]);

  return (
    <div className="App">
      <ToastContainer />
      {contextHolder}

      <Routes>
        <Route path="/" element={<Auth />} />
        <Route path="/unauthorized" element={<UnAuthorized />} />
        <Route element={<Layout />}>
          {routerComponents.map((router: any) => {
            const { path, Component } = router;
            return (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoutes>
                    <Component />
                  </ProtectedRoutes>
                }
              />
            );
          })}
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* ✅ Only show when clicked */}
      <GlobalFollowUpReminder
        visible={showReminder}
        onClose={() => setShowReminder(false)}
      />
    </div>
  );
}

export default App;
